import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';
import { platforms } from '@/mocks/platforms';

const categoryMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  jilupian: '纪录片',
  dongman: '动漫',
  zongyi: '综艺',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: categoryMap[article.category] || article.category, path: `/category/${article.category}` },
    { label: article.title },
  ];

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const relatedPlatforms = platforms.filter((p) =>
    p.tags.some((t) => article.tags.includes(t)) || p.categories.includes(article.category)
  ).slice(0, 3);

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '免费看片平台推荐',
    },
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://kexuetang.cn' },
      { '@type': 'ListItem', position: 2, name: categoryMap[article.category] || article.category, item: `https://kexuetang.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} | 免费看片平台推荐`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://kexuetang.cn/article/${slug}`}
        ogType="article"
        ogImage={article.imageUrl}
      />
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <Navbar />
      <main className="min-h-screen">
        <article className="max-w-4xl mx-auto px-4 md:px-6 pt-20 pb-6">
          <Breadcrumb items={breadcrumbItems} />
        </article>

        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-full whitespace-nowrap">
                {categoryMap[article.category] || article.category}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.readTime}分钟阅读</span>
              <span className="text-xs text-foreground-500">{article.author}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
          </div>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover max-h-[400px]"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none mb-12 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-12 pt-6 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {(relatedArticles.length > 0 || relatedPlatforms.length > 0) && (
          <section className="bg-background-100/60 py-10">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              {relatedPlatforms.length > 0 && (
                <div className="mb-10">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">相关平台推荐</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedPlatforms.map((p) => (
                      <Link
                        key={p.id}
                        to={`/article/${p.slug}`}
                        className="bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <img src={p.imageUrl} alt={p.name} className="w-14 h-14 rounded object-cover shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-foreground-900">{p.name}</p>
                            <div className="flex items-center gap-1 mt-1">
                              <i className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                              <span className="text-xs text-foreground-500">{p.rating}</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {relatedArticles.length > 0 && (
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">相关文章</h3>
                  <div className="space-y-3">
                    {relatedArticles.map((a) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.slug}`}
                        className="block bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-200 transition-all duration-300"
                      >
                        <h4 className="text-sm font-medium text-foreground-900 mb-1 hover:text-primary-500 transition-colors">
                          {a.title}
                        </h4>
                        <p className="text-xs text-foreground-500">{a.date} · {a.readTime}分钟阅读</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}